import React, { useState } from "react"
import { motion } from "framer-motion"
import {Check, X, Scale, ArrowRight} from 'lucide-react'
import { Link } from "react-router-dom"

interface Equipment {
  id: string
  name: string
  image: string
  category: string
  capacity: string
  hydraulicSystem: string
  controlType: string
  axleConfig: string
  terrainCapability: string
  dimensions: string
  weight: string
  powerSource: string
  liftHeight: string
  operatingSpeed: string
  maintenance: string
  idealFor: string
  priceRange: string
}

const ComparisonTool = () => {
  const [selectedProducts, setSelectedProducts] = useState<string[]>([])

  const equipmentData: Equipment[] = [
    {
      id: "mule-628t",
      name: "Mule 628T",
      image: "https://static.lumi.new/83/8323d6f5b2163d75439767d247c570f8.png",
      category: "Flagship Model",
      capacity: "28,000 lbs",
      hydraulicSystem: "Precision Hydraulic Lift",
      controlType: "Remote Control",
      axleConfig: "Dual-Axle",
      terrainCapability: "All-Terrain",
      dimensions: "180\" L × 96\" W × 72\" H",
      weight: "4,500 lbs",
      powerSource: "Diesel Engine",
      liftHeight: "48 inches",
      operatingSpeed: "5-8 mph",
      maintenance: "Standard (500 hrs)",
      idealFor: "Professional haulers, heavy sheds",
      priceRange: "$$$$"
    },
    {
      id: "mule-pro-520",
      name: "Mule Pro 520",
      image: "https://static.lumi.new/c3/c325335c821a43d843140cc603cc97e1.png",
      category: "Professional Series",
      capacity: "20,000 lbs",
      hydraulicSystem: "Standard Hydraulic",
      controlType: "Manual + Remote",
      axleConfig: "Single-Axle",
      terrainCapability: "Multi-Terrain",
      dimensions: "156\" L × 84\" W × 60\" H",
      weight: "3,200 lbs",
      powerSource: "Gasoline Engine",
      liftHeight: "36 inches",
      operatingSpeed: "4-6 mph",
      maintenance: "Standard (500 hrs)",
      idealFor: "Mid-sized operations, versatility",
      priceRange: "$$$"
    },
    {
      id: "mule-heavy-750",
      name: "Mule Heavy 750",
      image: "https://static.lumi.new/36/3661be1428baa055a82e994af7fba1ac.png",
      category: "Heavy-Duty",
      capacity: "35,000 lbs",
      hydraulicSystem: "Commercial-Grade Hydraulic",
      controlType: "Remote Control",
      axleConfig: "Triple-Axle",
      terrainCapability: "Extreme All-Terrain",
      dimensions: "204\" L × 108\" W × 84\" H",
      weight: "6,200 lbs",
      powerSource: "Heavy-Duty Diesel",
      liftHeight: "60 inches",
      operatingSpeed: "3-5 mph",
      maintenance: "Heavy-Duty (750 hrs)",
      idealFor: "Commercial operations, oversized buildings",
      priceRange: "$$$$$"
    }
  ]

  const toggleProduct = (productId: string) => {
    if (selectedProducts.includes(productId)) {
      setSelectedProducts(selectedProducts.filter(id => id !== productId))
    } else if (selectedProducts.length < 3) {
      setSelectedProducts([...selectedProducts, productId])
    }
  }

  const selectedEquipment = equipmentData.filter(eq => selectedProducts.includes(eq.id))

  const specifications = [
    { label: "Capacity", key: "capacity" as keyof Equipment },
    { label: "Hydraulic System", key: "hydraulicSystem" as keyof Equipment },
    { label: "Control Type", key: "controlType" as keyof Equipment },
    { label: "Axle Configuration", key: "axleConfig" as keyof Equipment },
    { label: "Terrain Capability", key: "terrainCapability" as keyof Equipment },
    { label: "Dimensions", key: "dimensions" as keyof Equipment },
    { label: "Weight", key: "weight" as keyof Equipment },
    { label: "Power Source", key: "powerSource" as keyof Equipment },
    { label: "Lift Height", key: "liftHeight" as keyof Equipment },
    { label: "Operating Speed", key: "operatingSpeed" as keyof Equipment },
    { label: "Maintenance Interval", key: "maintenance" as keyof Equipment },
    { label: "Ideal For", key: "idealFor" as keyof Equipment },
    { label: "Price Range", key: "priceRange" as keyof Equipment }
  ]

  return (
    <div className="bg-zinc-900 min-h-screen">
      {/* Header */}
      <section className="bg-zinc-950 py-20 border-b border-zinc-800">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center"
          >
            <div className="flex items-center justify-center gap-3 mb-6">
              <Scale className="text-orange-500" size={48} />
              <h1 className="text-5xl font-bold text-white">
                Equipment <span className="text-orange-500">Comparison Tool</span>
              </h1>
            </div>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto">
              Compare shed moving equipment side-by-side to understand specifications, capabilities, and find the right model for your needs
            </p>
          </motion.div>
        </div>
      </section>

      {/* Product Selection */}
      <section className="py-12 bg-zinc-800 border-b border-zinc-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-8">
            <h2 className="text-2xl font-bold text-white mb-2">
              Select Equipment to Compare
            </h2>
            <p className="text-gray-400">
              Choose up to 3 models ({selectedProducts.length}/3 selected)
            </p>
          </div>

          <div className="grid md:grid-cols-3 gap-6">
            {equipmentData.map((equipment) => {
              const isSelected = selectedProducts.includes(equipment.id)
              const isDisabled = !isSelected && selectedProducts.length >= 3

              return (
                <motion.button
                  key={equipment.id}
                  onClick={() => !isDisabled && toggleProduct(equipment.id)}
                  disabled={isDisabled}
                  className={`relative bg-zinc-900 rounded-lg overflow-hidden border-2 transition-all ${
                    isSelected
                      ? "border-orange-500 ring-4 ring-orange-500/30"
                      : isDisabled
                      ? "border-zinc-700 opacity-50 cursor-not-allowed"
                      : "border-zinc-700 hover:border-zinc-600"
                  }`}
                  whileHover={!isDisabled ? { scale: 1.02 } : {}}
                  whileTap={!isDisabled ? { scale: 0.98 } : {}}
                >
                  {isSelected && (
                    <div className="absolute top-3 right-3 z-10 bg-orange-500 rounded-full p-2">
                      <Check className="text-white" size={20} />
                    </div>
                  )}
                  <div className="h-48 overflow-hidden">
                    <img
                      src={equipment.image}
                      alt={equipment.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                  <div className="p-4">
                    <p className="text-orange-500 text-sm font-bold mb-1">{equipment.category}</p>
                    <h3 className="text-xl font-bold text-white mb-2">{equipment.name}</h3>
                    <p className="text-gray-400 text-sm">{equipment.capacity} Capacity</p>
                  </div>
                </motion.button>
              )
            })}
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      {selectedEquipment.length > 0 ? (
        <section className="py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <div className="bg-zinc-800 rounded-2xl border border-zinc-700 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-zinc-900">
                      <th className="text-left p-6 text-gray-400 font-bold border-b border-zinc-700">
                        Specification
                      </th>
                      {selectedEquipment.map((equipment) => (
                        <th key={equipment.id} className="p-6 border-b border-zinc-700">
                          <div className="text-center">
                            <img
                              src={equipment.image}
                              alt={equipment.name}
                              className="w-32 h-24 object-cover rounded-lg mx-auto mb-4"
                            />
                            <p className="text-orange-500 text-sm font-bold mb-1">
                              {equipment.category}
                            </p>
                            <h3 className="text-white font-bold text-lg">{equipment.name}</h3>
                          </div>
                        </th>
                      ))}
                    </tr>
                  </thead>
                  <tbody>
                    {specifications.map((spec, index) => (
                      <tr
                        key={spec.key}
                        className={index % 2 === 0 ? "bg-zinc-900/50" : "bg-zinc-800"}
                      >
                        <td className="p-4 text-gray-300 font-semibold border-b border-zinc-700">
                          {spec.label}
                        </td>
                        {selectedEquipment.map((equipment) => (
                          <td
                            key={equipment.id}
                            className="p-4 text-center text-white border-b border-zinc-700"
                          >
                            {equipment[spec.key]}
                          </td>
                        ))}
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="mt-8 flex flex-col sm:flex-row gap-4 justify-center">
              <Link
                to="/products"
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 flex items-center justify-center gap-2"
              >
                View Full Specs <ArrowRight />
              </Link>
              <Link
                to="/buying-guide"
                className="bg-zinc-700 hover:bg-zinc-600 text-white px-8 py-4 rounded-lg font-bold text-lg transition-all transform hover:scale-105 flex items-center justify-center gap-2"
              >
                Read Buying Guide
              </Link>
            </div>
          </div>
        </section>
      ) : (
        <section className="py-20">
          <div className="max-w-3xl mx-auto px-4 text-center">
            <div className="bg-zinc-800 rounded-2xl p-12 border border-zinc-700">
              <Scale className="text-orange-500 mx-auto mb-6" size={64} />
              <h3 className="text-2xl font-bold text-white mb-4">
                No Equipment Selected
              </h3>
              <p className="text-gray-400 text-lg">
                Select at least one equipment model above to start comparing specifications and features
              </p>
            </div>
          </div>
        </section>
      )}

      {/* Help Section */}
      <section className="py-20 bg-zinc-950">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="text-3xl font-bold text-white text-center mb-12">
            Comparison Guide
          </h2>
          <div className="grid md:grid-cols-2 gap-8">
            <div className="bg-zinc-800 p-6 rounded-lg border border-zinc-700">
              <h3 className="text-xl font-bold text-orange-500 mb-3">Capacity Considerations</h3>
              <p className="text-gray-300">
                Choose capacity based on typical shed sizes you'll be moving. Factor in shed weight plus contents for safety margin.
              </p>
            </div>
            <div className="bg-zinc-800 p-6 rounded-lg border border-zinc-700">
              <h3 className="text-xl font-bold text-orange-500 mb-3">Terrain Requirements</h3>
              <p className="text-gray-300">
                Multi-terrain models handle varied surfaces. All-terrain offers maximum flexibility for challenging sites.
              </p>
            </div>
            <div className="bg-zinc-800 p-6 rounded-lg border border-zinc-700">
              <h3 className="text-xl font-bold text-orange-500 mb-3">Axle Configuration</h3>
              <p className="text-gray-300">
                More axles provide better stability for heavier loads. Single-axle offers maneuverability, triple-axle maximizes capacity.
              </p>
            </div>
            <div className="bg-zinc-800 p-6 rounded-lg border border-zinc-700">
              <h3 className="text-xl font-bold text-orange-500 mb-3">Operating Costs</h3>
              <p className="text-gray-300">
                Consider fuel type, maintenance intervals, and expected usage frequency when evaluating total cost of operation.
              </p>
            </div>
          </div>
        </div>
      </section>
    </div>
  )
}

export default ComparisonTool
